/**
 * Validators for Brand / Browser / OperatingSystem conditions, which all share
 * the "optional simple list + optional pair-array + required predicate" shape.
 * For Media Devoted | Created By Ciro Autiero
 */

import { PREDICATE_VALUES } from '../enums';
import {
  describe,
  isPlainObject,
  isStringArray,
  joinPath,
  pushError,
  requireString,
  type ValidationCtx,
} from '../primitives';

function validateOptionalStringArray(
  ctx: ValidationCtx,
  v: unknown,
  basePath: string,
): void {
  if (v === undefined) return;
  if (!isStringArray(v)) {
    pushError(ctx, basePath, 'invalid_array', 'Expected string[]', {
      expected: 'string[]',
      received: describe(v),
    });
  }
}

function validatePairArray(
  ctx: ValidationCtx,
  v: unknown,
  basePath: string,
  fields: readonly [string, string],
): void {
  if (v === undefined) return;
  if (!Array.isArray(v)) {
    pushError(ctx, basePath, 'invalid_array', 'Expected array of pairs');
    return;
  }
  v.forEach((entry, idx) => {
    const path = joinPath(basePath, idx);
    if (!isPlainObject(entry)) {
      pushError(ctx, path, 'invalid_object', 'Pair must be an object');
      return;
    }
    requireString(ctx, entry, fields[0], path);
    requireString(ctx, entry, fields[1], path);
  });
}

export function validateBrandCondition(
  ctx: ValidationCtx,
  raw: Record<string, unknown>,
  basePath: string,
): void {
  validateOptionalStringArray(ctx, raw.brands, joinPath(basePath, 'brands'));
  validatePairArray(
    ctx,
    raw.brandAndModels,
    joinPath(basePath, 'brandAndModels'),
    ['brand', 'model'],
  );
  requireString(ctx, raw, 'predicate', basePath, PREDICATE_VALUES);
}

export function validateBrowserCondition(
  ctx: ValidationCtx,
  raw: Record<string, unknown>,
  basePath: string,
): void {
  validateOptionalStringArray(ctx, raw.browsers, joinPath(basePath, 'browsers'));
  validatePairArray(
    ctx,
    raw.browserAndVersions,
    joinPath(basePath, 'browserAndVersions'),
    ['browser', 'version'],
  );
  requireString(ctx, raw, 'predicate', basePath, PREDICATE_VALUES);
}

export function validateOperatingSystemCondition(
  ctx: ValidationCtx,
  raw: Record<string, unknown>,
  basePath: string,
): void {
  validateOptionalStringArray(
    ctx,
    raw.operatingSystems,
    joinPath(basePath, 'operatingSystems'),
  );
  validatePairArray(
    ctx,
    raw.operatingSystemsAndVersions,
    joinPath(basePath, 'operatingSystemsAndVersions'),
    ['operatingSystem', 'version'],
  );
  requireString(ctx, raw, 'predicate', basePath, PREDICATE_VALUES);
}
